import { useNavigate } from 'react-router'
import { SlidersHorizontal } from 'lucide-react'
import { PageHeader } from '@/components/data/page-header'
import { FlagChips, PlanBadge, StateBadge, SyncStatus } from '@/components/data/badges'
import { FilterSelect, PAGE_SIZE, Pagination, SearchBox, SortHead, ToggleChip } from '@/components/data/list-controls'
import { EmptyState, ErrorState, SkeletonRows, Warnings } from '@/components/data/states'
import { CompaniesCell, MembersCell, TeamNameCell } from '@/components/data/team-cells'
import { TeamDrawer } from '@/components/data/team-drawer'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHeader, TableRow, TableHead } from '@/components/ui/table'
import { TooltipProvider } from '@/components/ui/tooltip'
import { useListParams } from '@/hooks/use-list-params'
import { formatRelative } from '@/lib/format'
import { flagLabels, stateLabels, syncLabels } from '@/lib/labels'
import { useTeams } from '@/lib/queries'
import type { Flag } from '@/lib/types'

const defaults = { sort: 'last_active', order: 'desc' }

export default function TeamsPage() {
  const navigate = useNavigate()
  const { params, set } = useListParams(defaults)
  const { data, error, isPending, isFetching, refetch } = useTeams(params)
  const offset = Number(params.offset ?? 0)
  const open = data?.items.find((t) => String(t.id) === params.team)

  const filtered = ['plan', 'state', 'sync', 'flag', 'attention', 'search'].some((k) => params[k])
  const onSort = (sort: string, order: string) => set({ sort, order })
  const sortProps = { sort: params.sort, order: params.order, onSort }

  return (
    <TooltipProvider>
      <PageHeader title="Teams">
        {data ? (
          <>
            {data.total.toLocaleString('en-US')} {filtered ? 'matching' : 'in total'}
          </>
        ) : (
          'Workspaces, their plans and how much they use.'
        )}
      </PageHeader>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <SearchBox value={params.search ?? ''} onChange={(search) => set({ search })} placeholder="Team, owner, member email, Stripe id" />
        <FilterSelect
          label="Plan"
          value={params.plan ?? ''}
          onChange={(plan) => set({ plan })}
          options={[
            { value: 'free', label: 'Free' },
            { value: 'paid', label: 'Any paid plan' },
            { value: 'premium_monthly', label: 'Premium monthly' },
            { value: 'premium_yearly', label: 'Premium yearly' },
          ]}
        />
        <FilterSelect
          label="Subscription"
          value={params.state ?? ''}
          onChange={(state) => set({ state })}
          options={(['active', 'expiring', 'canceling', 'expired', 'free'] as const).map((s) => ({ value: s, label: stateLabels[s] }))}
        />
        <FilterSelect
          label="HubSpot"
          value={params.sync ?? ''}
          onChange={(sync) => set({ sync })}
          options={[{ value: 'none', label: 'Not connected' }, ...Object.entries(syncLabels).map(([value, label]) => ({ value, label }))]}
        />
        <FilterSelect
          label="Flag"
          value={params.flag ?? ''}
          onChange={(flag) => set({ flag })}
          options={(Object.keys(flagLabels) as Flag[]).map((f) => ({ value: f, label: flagLabels[f].label }))}
        />
        <ToggleChip pressed={params.attention === '1'} onChange={(on) => set({ attention: on ? '1' : '' })}>
          <SlidersHorizontal className="size-3.5" /> Needs attention
        </ToggleChip>
        {filtered && (
          <Button variant="ghost" size="sm" onClick={() => set({ search: '', plan: '', state: '', sync: '', flag: '', attention: '' })}>
            Clear filters
          </Button>
        )}
      </div>

      <Warnings warnings={data?.warnings} />

      <div className="panel overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <SortHead id="name" {...sortProps}>
                  Team
                </SortHead>
                <SortHead id="plan" {...sortProps}>
                  Plan
                </SortHead>
                <SortHead id="members" {...sortProps}>
                  Members
                </SortHead>
                <SortHead id="companies" {...sortProps}>
                  Companies
                </SortHead>
                <TableHead>HubSpot</TableHead>
                <SortHead id="last_active" {...sortProps}>
                  Last active
                </SortHead>
                <SortHead id="attention" {...sortProps}>
                  Flags
                </SortHead>
              </TableRow>
            </TableHeader>
            <TableBody className={isFetching && !isPending ? 'opacity-70' : undefined}>
              {isPending ? (
                <SkeletonRows columns={7} />
              ) : (
                data?.items.map((t) => (
                  <TableRow
                    key={t.id}
                    tabIndex={0}
                    onClick={() => set({ team: String(t.id) }) }
                    onDoubleClick={() => navigate(`/teams/${t.id}`)}
                    onKeyDown={(e) => e.key === 'Enter' && set({ team: String(t.id) })}
                    className="cursor-pointer"
                  >
                    <TableCell className="max-w-64">
                      <TeamNameCell team={t} />
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col items-start gap-1">
                        <PlanBadge planId={t.plan_id} name={t.plan_name} trial={t.subscription.source === 'trial'} />
                        {t.subscription.state !== 'free' && <StateBadge state={t.subscription.state} />}
                      </div>
                    </TableCell>
                    <TableCell>
                      <MembersCell team={t} />
                    </TableCell>
                    <TableCell>
                      <CompaniesCell team={t} />
                    </TableCell>
                    <TableCell>
                      <SyncStatus status={t.hubspot?.status} />
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground">{formatRelative(t.last_active_at)}</TableCell>
                    <TableCell>
                      <FlagChips flags={t.flags} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {error && <ErrorState error={error} onRetry={() => refetch()} />}
        {!isPending && !error && data?.items.length === 0 && (
          <EmptyState
            title={filtered ? 'No team matches these filters' : 'No teams yet'}
            action={
              filtered && (
                <Button variant="outline" size="sm" onClick={() => set({ search: '', plan: '', state: '', sync: '', flag: '', attention: '' })}>
                  Clear filters
                </Button>
              )
            }
          >
            {filtered ? 'Widen the search or remove a filter.' : 'Teams appear here once someone signs up.'}
          </EmptyState>
        )}
        <Pagination total={data?.total ?? 0} offset={offset} pageSize={PAGE_SIZE} onChange={(o) => set({ offset: String(o) })} />
      </div>

      <TeamDrawer team={open} onClose={() => set({ team: '' })} />
    </TooltipProvider>
  )
}
